import {Fragment, useRef, useState} from 'react';
import Button from "../UI/Button";
import React from "react";
import Modal from "../UI/Modal";
import {createCoupon, deleteCoupon, getCoupon, updateCoupon} from "../../api/eventGroupApiClient";
import ListEdit from "../UI/ListEdit";
import ModalForm from "../UI/ModalForm";
import ModalFormInputRow from "../UI/ModalFormInputRow";
import {toast} from "react-toastify";
import {useProducts} from "../../hooks/useProducts";
import {
    convertToDisplayDate,
    convertToDisplayDateTime,
    convertToYmdHis,
    getPaymentMethodLabel,
    PAYMENT_METHOD_CREDIT_CARD,
    PAYMENT_METHOD_DIRECT_DEBIT,
    PAYMENT_METHOD_MOLLIE_PAY_BY_BANK,
    PAYMENT_METHOD_PAYPAL,
    toDatetimeLocalValue
} from "../../utility/utility";
import {useCoupons} from "../../hooks/useCoupons";
import {useCustomers} from "../../hooks/useCustomers";
import {useItems} from "../../hooks/useItems";
import Select from 'react-select';
import {useNavigate} from "react-router-dom";

// The API rejects any other payment method on a coupon.
const COUPON_PAYMENT_METHODS = [
    PAYMENT_METHOD_PAYPAL,
    PAYMENT_METHOD_CREDIT_CARD,
    PAYMENT_METHOD_DIRECT_DEBIT,
    PAYMENT_METHOD_MOLLIE_PAY_BY_BANK,
];

const COUPON_TYPE_PRODUCT = 'product';
const COUPON_TYPE_ITEM = 'item';

const COUPON_TYPE_LABELS = {
    [COUPON_TYPE_PRODUCT] : 'Product',
    [COUPON_TYPE_ITEM]    : 'Webshop item',
};

const formatDiscount = (amount, percentage) => {
    if (percentage !== null) {
        return percentage + ' %';
    }
    if (amount !== null) {
        return amount + ' eur';
    }
    return null;
};

const formatPaymentMethods = (coupon) => {
    if (coupon.isAnyPaymentMethod) {
        return 'Any';
    }

    return coupon.paymentMethods
        .map((row) => {
            const override = formatDiscount(row.amount, row.percentage);
            return override ? `${row.displayName} (${override})` : row.displayName;
        })
        .join(', ');
};

const toInputValue = (number) => number === null ? '' : String(number);
const toNumberOrNull = (inputValue) => inputValue === '' ? null : Number(inputValue);

const CouponList = (props) => {
    const [showModal, setShowModal] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [isPublic, setIsPublic] = useState(true);
    const [isGlobal, setIsGlobal] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [coupon, setCoupon] = useState(null);
    const [submitCount, setSubmitCount] = useState(0);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [type, setType] = useState(COUPON_TYPE_PRODUCT);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isAnyPaymentMethod, setIsAnyPaymentMethod] = useState(true);
    // Keyed by payment method; a key is present only for selected methods.
    // Empty strings mean "no override", so the coupon's own discount applies.
    const [selectedPaymentMethods, setSelectedPaymentMethods] = useState({});
    const [filterCustomerFirstName, setFilterCustomerFirstName] = useState(null);
    const [filterProductName, setFilterProductName] = useState(null);
    const codeRef = useRef(null);
    const nameRef = useRef(null);
    const amountRef = useRef(null);
    const percentageRef = useRef(null);
    const validFromRef = useRef(null);
    const validToRef = useRef(null);
    const availableCountRef = useRef(null);
    const maxUsesPerCustomerRef = useRef(null);
    const isPublicRef = useRef(null);
    const isGlobalRef = useRef(null);
    const singleUsePerBookingGroupRef = useRef(null);
    const {products} = useProducts(null, filterProductName, 1, 100, 0);
    // GET /api/items has no name filter, so the item picker searches only this first page.
    const {items} = useItems(null, null, 1, 100, 0);
    const {customers} = useCustomers(1, 100, filterCustomerFirstName, null, null, null, 0);
    const {coupons, pagination} = useCoupons(page, pageSize, submitCount);
    const navigate = useNavigate();

    const couponList = coupons.map((item) => {
        return {
            couponId                 : item.couponId,
            name                     : item.name,
            code                     : item.code,
            type                     : COUPON_TYPE_LABELS[item.type],
            validFrom                : convertToDisplayDate(item.validFrom),
            validTo                  : convertToDisplayDate(item.validTo),
            percentage               : item.percentage ? item.percentage + ' %' : '',
            amount                   : item.amount ? item.amount + ' eur' : '',
            customerNames            : item.customers.map(customer => customer.customerName).join(', '),
            productNames             : item.products.map(product => product.productName).join(', '),
            itemNames                : item.items.map(couponItem => couponItem.itemName).join(', '),
            availableCount           : item.availableCount,
            maxUsesPerCustomer       : item.maxUsesPerCustomer,
            isPublic                 : item.isPublic ? 'Yes' : 'No',
            isGlobal                 : item.isGlobal ? 'Yes' : 'No',
            archivedAt               : item.archivedAt ? convertToDisplayDateTime(item.archivedAt) : null,
            singleUsePerBookingGroup : item.singleUsePerBookingGroup ? 'Yes' : 'No',
            paymentMethods           : formatPaymentMethods(item),
        };
    });

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    };

    const handlePageSizeChange = (pageSize) => {
        setPageSize(pageSize);
        setPage(1);
    };

    const handleEditIconClick = (couponId) => {
        setIsUpdate(true)
        const coupon = getCoupon(couponId);

        coupon
            .then(
                (data) => {
                    setCoupon(data)
                    setIsPublic(data.isPublic)
                    setIsGlobal(data.isGlobal)
                    setType(data.type)

                    if (data.customers && data.customers.length > 0) {
                        const customerOptions = data.customers.map(customer => ({
                            value : customer.customerId,
                            label : `${customer.customerName} (${customer.customerEmail || 'No email'})`
                        }));
                        setSelectedCustomers(customerOptions);
                    }
                    else {
                        setSelectedCustomers([]);
                    }

                    if (data.products && data.products.length > 0) {
                        const productOptions = data.products.map(product => ({
                            value : product.productId,
                            label : product.productName
                        }));
                        setSelectedProducts(productOptions);
                    }
                    else {
                        setSelectedProducts([]);
                    }

                    setSelectedItems(data.items.map(couponItem => ({
                        value : couponItem.itemId,
                        label : couponItem.itemName
                    })));

                    setIsAnyPaymentMethod(data.isAnyPaymentMethod);
                    setSelectedPaymentMethods(Object.fromEntries(
                        data.paymentMethods.map(row => [
                            row.paymentMethod,
                            {amount : toInputValue(row.amount), percentage : toInputValue(row.percentage)}
                        ])
                    ));

                    setShowModal(true)
                }
            )
            .catch(
                (error) => console.log(error)
            );
    }

    const handleUsagesIconClick = (couponId) => {
        navigate(`/coupons?view=usages&couponId=${couponId}`);
    }

    const handleCreateButtonClick = () => {
        // Reset form fields
        if (nameRef.current) {
            nameRef.current.value = '';
        }
        if (codeRef.current) {
            codeRef.current.value = '';
        }
        if (amountRef.current) {
            amountRef.current.value = '';
        }
        if (percentageRef.current) {
            percentageRef.current.value = '';
        }
        if (validFromRef.current) {
            validFromRef.current.value = '';
        }
        if (validToRef.current) {
            validToRef.current.value = '';
        }
        if (availableCountRef.current) {
            availableCountRef.current.value = '1';
        }
        if (maxUsesPerCustomerRef.current) {
            maxUsesPerCustomerRef.current.value = '1';
        }
        if (isPublicRef.current) {
            isPublicRef.current.checked = true;
        }
        if (isGlobalRef.current) {
            isGlobalRef.current.checked = true;
        }
        if (singleUsePerBookingGroupRef.current) {
            singleUsePerBookingGroupRef.current.checked = false;
        }

        // Reset state
        setIsPublic(true);
        setIsGlobal(true);
        setSelectedCustomers([]);
        setType(COUPON_TYPE_PRODUCT);
        setSelectedProducts([]);
        setSelectedItems([]);
        setIsAnyPaymentMethod(true);
        setSelectedPaymentMethods({});

        setIsUpdate(false);
        setShowModal(true);
    }

    const paymentMethodsPayload = () => {
        if (isAnyPaymentMethod) {
            return [];
        }

        return Object.entries(selectedPaymentMethods).map(([paymentMethod, override]) => ({
            paymentMethod : paymentMethod,
            amount        : toNumberOrNull(override.amount),
            percentage    : toNumberOrNull(override.percentage),
        }));
    }

    const couponPayload = () => {
        const isPublic = isPublicRef.current.checked ? 1 : 0;
        const isGlobal = isGlobalRef.current.checked ? 1 : 0;
        const isItemCoupon = type === COUPON_TYPE_ITEM;
        const toIds = (options) => options.map(option => parseInt(option.value, 10));

        return {
            type                     : type,
            code                     : codeRef.current.value,
            name                     : nameRef.current.value,
            amount                   : amountRef.current.value || null,
            percentage               : percentageRef.current.value || null,
            validFrom                : validFromRef.current.value ? convertToYmdHis(validFromRef.current.value) : null,
            validTo                  : validToRef.current.value ? convertToYmdHis(validToRef.current.value) : null,
            availableCount           : availableCountRef.current.value || 1,
            maxUsesPerCustomer       : maxUsesPerCustomerRef.current.value || 1,
            isPublic                 : isPublic,
            isGlobal                 : isGlobal,
            // The API rejects singleUsePerBookingGroup on item coupons; the checkbox is hidden for them.
            singleUsePerBookingGroup : !isItemCoupon && singleUsePerBookingGroupRef.current.checked ? 1 : 0,
            customerIds              : isPublic === 0 ? toIds(selectedCustomers) : [],
            productIds               : isGlobal === 0 && !isItemCoupon ? toIds(selectedProducts) : [],
            itemIds                  : isGlobal === 0 && isItemCoupon ? toIds(selectedItems) : [],
            isAnyPaymentMethod       : isAnyPaymentMethod,
            paymentMethods           : paymentMethodsPayload(),
        };
    }

    const handleSaved = () => {
        setShowModal(false)
        setSubmitCount(prevCount => prevCount + 1);
        toast.success('Success!');
    }

    const handleCreateSubmit = (event) => {
        event.preventDefault();

        createCoupon(couponPayload())
            .then(handleSaved)
            .catch(
                (error) => {
                    toast.error(error.message);
                }
            );
    }

    const handleEditSubmit = (event) => {
        event.preventDefault();

        updateCoupon(coupon.couponId, couponPayload())
            .then(handleSaved)
            .catch(
                (error) => {
                    toast.error(error.message);
                }
            );
    }

    const listHeaders = [
        'Id',
        'Name',
        'Code',
        'Type',
        'Valid from',
        'Valid to',
        'Percentage',
        'Amount',
        'Customers',
        'Products',
        'Items',
        'Available count',
        'Max uses per customer',
        'Is public',
        'Is global',
        'Single use per booking group',
        'Payment methods',
        'Archived at',
        'Actions'
    ];

    const columns = {
        functionParams : ['couponId'],
        dataKeys       : [
            'couponId',
            'name',
            'code',
            'type',
            'validFrom',
            'validTo',
            'percentage',
            'amount',
            'customerNames',
            'productNames',
            'itemNames',
            'availableCount',
            'maxUsesPerCustomer',
            'isPublic',
            'isGlobal',
            'singleUsePerBookingGroup',
            'paymentMethods',
            'archivedAt',
        ],
    }

    const handleIsPublicChange = () => {
        setIsPublic(isPublicRef.current.checked);
    }

    const handleIsGlobalChange = () => {
        setIsGlobal(isGlobalRef.current.checked);
    }

    const handlePaymentMethodToggle = (paymentMethod) => {
        setSelectedPaymentMethods((previous) => {
            const {[paymentMethod]: removed, ...rest} = previous;
            return removed ? rest : {...previous, [paymentMethod] : {amount : '', percentage : ''}};
        });
    }

    const handlePaymentMethodOverrideChange = (paymentMethod, field, value) => {
        setSelectedPaymentMethods((previous) => ({
            ...previous,
            [paymentMethod] : {...previous[paymentMethod], [field] : value}
        }));
    }

    const handleDeleteIconClick = (couponId) => {
        if (window.confirm('Are you sure you want to archive this coupon?')) {
            deleteCoupon(couponId).then(
                () => {
                    setSubmitCount(prevCount => prevCount + 1);
                    toast.success('Success!');
                }
            ).catch((error) => {
                toast.error(error.message);
            });
        }
    }

    return <Fragment>
        <div className="my-4">
            <Button title='Create coupon' handleClick={handleCreateButtonClick}/>
        </div>
        <ListEdit
            headers={listHeaders}
            items={couponList}
            columns={columns}
            pagination={pagination}
            handleEditIconClick={handleEditIconClick}
            handleUsagesIconClick={handleUsagesIconClick}
            handleDeleteIconClick={handleDeleteIconClick}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
        />
        {showModal && (
            <Modal>
                <form onSubmit={isUpdate ? handleEditSubmit : handleCreateSubmit}>
                    <ModalForm
                        title={isUpdate ? 'Update coupon' : 'Create coupon'}
                        saveButtonTitle={isUpdate ? 'Update' : 'Create'}
                        handleCancelButtonClick={() => setShowModal(false)}
                    >
                        <ModalFormInputRow title='Name'>
                            <input
                                ref={nameRef}
                                type="text"
                                id="name"
                                name="name"
                                defaultValue={isUpdate ? coupon.name : null}
                                placeholder="Enter name"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>

                        <ModalFormInputRow title='Code'>
                            <input
                                ref={codeRef}
                                type="text"
                                id="code"
                                name="code"
                                defaultValue={isUpdate ? coupon.code : null}
                                placeholder="Enter code"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Type'>
                            {isUpdate ? (
                                // The API does not let a coupon change its type after creation.
                                <p className="py-2 text-gray-700">{COUPON_TYPE_LABELS[type]}</p>
                            ) : (
                                <select
                                    id="type"
                                    name="type"
                                    value={type}
                                    onChange={(event) => setType(event.target.value)}
                                    className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                >
                                    {Object.entries(COUPON_TYPE_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            )}
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Amount'>
                            <input
                                ref={amountRef}
                                type="number"
                                id="amount"
                                name="amount"
                                defaultValue={isUpdate ? coupon.amount : null}
                                placeholder="Enter amount"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Percentage'>
                            <input
                                ref={percentageRef}
                                type="number"
                                id="percentage"
                                name="percentage"
                                defaultValue={isUpdate ? coupon.percentage : null}
                                placeholder="Enter percentage. e.g. 10"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Valid from'>
                            <input
                                ref={validFromRef}
                                type="datetime-local"
                                id="validFrom"
                                name="validFrom"
                                step="1"
                                defaultValue={isUpdate ? toDatetimeLocalValue(coupon.validFrom) : null}
                                placeholder="Enter valid from"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Valid to'>
                            <input
                                ref={validToRef}
                                type="datetime-local"
                                id="validTo"
                                name="validTo"
                                step="1"
                                defaultValue={isUpdate ? toDatetimeLocalValue(coupon.validTo) : null}
                                placeholder="Enter valid to"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Available count'>
                            <input
                                ref={availableCountRef}
                                type="number"
                                id="availableCount"
                                name="availableCount"
                                defaultValue={isUpdate ? coupon.availableCount : 1}
                                placeholder="Enter available count"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Max uses per customer'>
                            <input
                                ref={maxUsesPerCustomerRef}
                                type="number"
                                id="maxUsesPerCustomer"
                                name="maxUsesPerCustomer"
                                defaultValue={isUpdate ? coupon.maxUsesPerCustomer : 1}
                                placeholder="Enter max uses per customer"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Is public'>
                            <input
                                ref={isPublicRef}
                                type="checkbox"
                                id="isPublic"
                                name="isPublic"
                                defaultChecked={isUpdate ? coupon.isPublic : isPublic}
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                onChange={handleIsPublicChange}
                            />
                        </ModalFormInputRow>
                        {isPublic === false && (
                            <Fragment>
                                <ModalFormInputRow title="Customers">
                                    {customers && (!isUpdate || coupon) && (
                                        <Select
                                            isMulti
                                            value={selectedCustomers}
                                            onChange={setSelectedCustomers}
                                            options={customers.map(customer => ({
                                                value : customer.id,
                                                label : `${customer.firstName} ${customer.lastName} ${customer.email ? `(${customer.email})` : ''}`
                                            }))}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select customers..."
                                            onInputChange={(inputValue) => {
                                                if (inputValue.length >= 3) {
                                                    setFilterCustomerFirstName(inputValue);
                                                }
                                                else if (inputValue.length === 0) {
                                                    setFilterCustomerFirstName(null);
                                                }
                                            }}
                                            filterOption={() => true} // Disable client-side filtering, we use server-side
                                            menuPortalTarget={document.body}
                                            styles={{menuPortal : (base) => ({...base, zIndex : 9999})}}
                                        />
                                    )}
                                </ModalFormInputRow>
                            </Fragment>
                        )}
                        <ModalFormInputRow title='Is global'>
                            <input
                                ref={isGlobalRef}
                                type="checkbox"
                                id="isGlobal"
                                name="isGlobal"
                                defaultChecked={isUpdate ? coupon.isGlobal : isGlobal}
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                onChange={handleIsGlobalChange}
                            />
                        </ModalFormInputRow>

                        {isGlobal === false && type !== COUPON_TYPE_ITEM && (
                            <Fragment>
                                <ModalFormInputRow title="Products">
                                    {products && (!isUpdate || coupon) && (
                                        <Select
                                            isMulti
                                            value={selectedProducts}
                                            onChange={setSelectedProducts}
                                            options={products.map(product => ({
                                                value : product.productId,
                                                label : product.name
                                            }))}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            placeholder="Select products..."
                                            onInputChange={(inputValue) => {
                                                if (inputValue.length >= 3) {
                                                    setFilterProductName(inputValue);
                                                }
                                                else if (inputValue.length === 0) {
                                                    setFilterProductName(null);
                                                }
                                            }}
                                            filterOption={() => true} // Disable client-side filtering, we use server-side
                                            menuPortalTarget={document.body}
                                            styles={{menuPortal : (base) => ({...base, zIndex : 9999})}}
                                        />
                                    )}
                                </ModalFormInputRow>
                            </Fragment>
                        )}
                        {isGlobal === false && type === COUPON_TYPE_ITEM && (
                            <ModalFormInputRow title="Items">
                                <Select
                                    isMulti
                                    value={selectedItems}
                                    onChange={setSelectedItems}
                                    options={items.map(item => ({
                                        value : item.itemId,
                                        label : item.name
                                    }))}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    placeholder="Select items..."
                                    menuPortalTarget={document.body}
                                    styles={{menuPortal : (base) => ({...base, zIndex : 9999})}}
                                />
                            </ModalFormInputRow>
                        )}
                        <ModalFormInputRow title='Any payment method'>
                            <input
                                type="checkbox"
                                id="isAnyPaymentMethod"
                                name="isAnyPaymentMethod"
                                checked={isAnyPaymentMethod}
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                onChange={(event) => setIsAnyPaymentMethod(event.target.checked)}
                            />
                        </ModalFormInputRow>
                        {isAnyPaymentMethod === false && COUPON_PAYMENT_METHODS.map((paymentMethod) => {
                            const override = selectedPaymentMethods[paymentMethod];

                            // Amount and percentage overrides are mutually exclusive,
                            // so filling one disables the other.
                            return <ModalFormInputRow key={paymentMethod} title={getPaymentMethodLabel(paymentMethod)}>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(override)}
                                        onChange={() => handlePaymentMethodToggle(paymentMethod)}
                                    />
                                    {override && (
                                        <Fragment>
                                            <input
                                                type="number"
                                                value={override.amount}
                                                disabled={override.percentage !== ''}
                                                onChange={(event) => handlePaymentMethodOverrideChange(paymentMethod, 'amount', event.target.value)}
                                                placeholder="Amount override"
                                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline disabled:bg-gray-100"
                                            />
                                            <input
                                                type="number"
                                                value={override.percentage}
                                                disabled={override.amount !== ''}
                                                onChange={(event) => handlePaymentMethodOverrideChange(paymentMethod, 'percentage', event.target.value)}
                                                placeholder="Percentage override"
                                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline disabled:bg-gray-100"
                                            />
                                        </Fragment>
                                    )}
                                </div>
                            </ModalFormInputRow>;
                        })}
                        {type !== COUPON_TYPE_ITEM && (
                            <ModalFormInputRow title='Single use per booking group'>
                                <input
                                    ref={singleUsePerBookingGroupRef}
                                    type="checkbox"
                                    id="singleUsePerBookingGroup"
                                    name="singleUsePerBookingGroup"
                                    defaultChecked={isUpdate ? coupon.singleUsePerBookingGroup : false}
                                    className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                />
                            </ModalFormInputRow>
                        )}
                    </ModalForm>
                </form>
            </Modal>
        )}
    </Fragment>;
}

export default CouponList;